import { Box, useTheme, Button, Typography } from '@mui/material';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { track } from '@/third-party/MixPanel';

interface DailyReadingEmptyStateProps {
    onChooseCard: () => void;
}

const DailyReadingEmptyState: React.FC<DailyReadingEmptyStateProps> = ({ onChooseCard }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Box sx={{
            width: '100%',
            display: 'flex',
            gap: 1,
            padding: '0px',
            alignItems: 'flex-start',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                padding: '20px',
                minWidth: "100%",
                background: theme.palette.background.accent,
                borderRadius: '16px',
                border: `1px dashed ${theme.palette.accent.main}66`,
                position: 'relative',
                overflow: 'hidden',
                minHeight: '160px',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '100px',
                    height: '100px',
                    background: `radial-gradient(circle, ${theme.palette.accent.main}15 0%, transparent 70%)`,
                    borderRadius: '50%',
                    pointerEvents: 'none',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-30%',
                    left: '-30%',
                    width: '80px',
                    height: '80px',
                    background: `radial-gradient(circle, ${theme.palette.accent.main}15 0%, transparent 70%)`,
                    borderRadius: '50%',
                    pointerEvents: 'none',
                }
            }}>
                <Box sx={{
                    display: 'flex',
                    gap: 0.5,
                    opacity: 0.3,
                    mb: 0.5
                }}>
                    <Sparkles size={12} />
                    <Sparkles size={16} />
                    <Sparkles size={12} />
                </Box>

                <Typography sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '0.85rem',
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    fontStyle: 'italic',
                    lineHeight: 1.5,
                    maxWidth: '90%',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {t('home.dailyReadingPrompt')}
                </Typography>

                <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                        track('daily_reading_choose_card_clicked');
                        onChooseCard();
                    }}
                    sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontSize: '0.85rem',
                        py: 1.5,
                        px: 3,
                        borderRadius: '12px',
                        textTransform: 'none',
                        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        position: 'relative',
                        zIndex: 1,
                        '&:hover': {
                            boxShadow: `0 6px 16px ${theme.palette.primary.main}40`,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                        }
                    }}
                >
                    <Sparkles
                        className="animate-pulse"
                        style={{
                            width: '18px',
                            height: '18px',
                            marginRight: '8px',
                        }}
                    />
                    {t('home.chooseCard')}
                </Button>
            </Box>
        </Box>
    );
};

export default DailyReadingEmptyState;

